import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatusBadge from '../components/StatusBadge';
import { Loading, ErrorState, EmptyState } from '../components/States';
import Icon from '../components/Icon';
import { ICONS } from '../lib/icons';

const PROVIDER_ID = 'oci';

// VaultDetail lists the keys inside a single vault and creates new software
// keys. It is reached from the Vaults console.
export default function VaultDetail() {
  const { vaultId } = useParams();
  const [params, setParams] = useSearchParams();
  const { setNav } = useSideNav();

  const { data, loading, error, reload } = useAsync(
    () => api.getVaultKeys(PROVIDER_ID, vaultId),
    [vaultId]
  );

  useEffect(() => {
    setNav({
      title: 'Vault',
      subtitle: 'Key Management',
      backTo: '/vaults',
      items: [
        { label: 'Vaults', to: '/vaults', icon: ICONS.list },
        { label: 'Keys', to: `/vaults/${encodeURIComponent(vaultId)}`, icon: ICONS.key },
        {
          label: 'Create key',
          to: `/vaults/${encodeURIComponent(vaultId)}?action=create`,
          icon: ICONS.create,
        },
      ],
    });
  }, [setNav, vaultId]);
  useEffect(() => () => setNav(null), [setNav]);

  const showCreate = params.get('action') === 'create';
  const openCreate = () => setParams({ action: 'create' });
  const closeCreate = () => {
    const next = new URLSearchParams(params);
    next.delete('action');
    setParams(next, { replace: true });
  };

  if (loading) return <Loading label="Loading keys…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const keys = data || [];

  return (
    <div className="stack">
      <PageHeader
        title="Keys"
        subtitle={vaultId}
        actions={
          <button className="btn btn--primary" onClick={openCreate}>
            + Create key
          </button>
        }
      />

      <Card title={`Keys (${keys.length})`}>
        {keys.length === 0 ? (
          <EmptyState
            title="No keys yet"
            detail="Create a software-protected master encryption key in this vault."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>OCID</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td className="cell-primary">{k.displayName}</td>
                    <td>
                      <StatusBadge status={k.state} />
                    </td>
                    <td className="cell-sub mono">{k.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <CreateKeyDialog
          vaultId={vaultId}
          onClose={closeCreate}
          onCreated={() => {
            closeCreate();
            reload();
          }}
        />
      )}
    </div>
  );
}

// Default key length (in bytes) per algorithm — mirrors the OCI KMS defaults.
const DEFAULT_LENGTH = { AES: 32, RSA: 256, ECDSA: 32 };

// CreateKeyDialog provisions a software-protected master key in the vault.
function CreateKeyDialog({ vaultId, onClose, onCreated }) {
  const [form, setForm] = useState({ displayName: '', algorithm: 'AES', length: 32 });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const onAlgorithm = (e) => {
    const algorithm = e.target.value;
    setForm((f) => ({
      ...f,
      algorithm,
      length: DEFAULT_LENGTH[algorithm] || f.length,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createVaultKey(PROVIDER_ID, vaultId, {
        displayName: form.displayName,
        algorithm: form.algorithm,
        length: Number(form.length),
      });
      onCreated();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel">
        <header className="modal__header">
          <h2>Create key</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name={ICONS.close} />
          </button>
        </header>

        <form className="modal__body form" onSubmit={submit}>
          <label className="field">
            <span>Display name</span>
            <input
              className="input"
              placeholder="e.g. app-data-key"
              value={form.displayName}
              onChange={update('displayName')}
              required
            />
          </label>

          <div className="form-grid">
            <label className="field">
              <span>Algorithm</span>
              <select className="input" value={form.algorithm} onChange={onAlgorithm}>
                <option value="AES">AES</option>
                <option value="RSA">RSA</option>
                <option value="ECDSA">ECDSA</option>
              </select>
            </label>

            <label className="field">
              <span>Length (bytes)</span>
              <input
                className="input"
                type="number"
                min="1"
                value={form.length}
                onChange={update('length')}
                required
              />
            </label>
          </div>

          {error ? <div className="form-error">{error}</div> : null}

          <footer className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create key'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
